import {
  cast,
  flow,
  getParentOfType,
  getSnapshot,
  Instance,
  SnapshotIn,
  types,
} from 'mobx-state-tree'
import { getChoicesByQuestionType } from '../utils'
import { Factoid } from './Factoid'
import { ChoicePair, QuestionChoice } from './QuestionChoice'
import { QuestionType } from './QuestionType'
import { QuestionApi } from '../services/api-objects'
import { Quiz } from './Quiz'

export const Question = types
  .model('Question')
  .props({
    keep_answer_order: types.boolean,
    shuffle: types.maybe(types.boolean),
    adaptive_eligible: types.maybeNull(types.boolean),
    difficulty: types.number,
    factoid: types.maybeNull(Factoid),
    is_locked: types.maybe(types.boolean),
    question_id: types.maybeNull(types.number),
    card_id: types.maybeNull(types.number),
    question_type: QuestionType,
    choices: types.array(QuestionChoice),
    text: types.string,
    alt_text: types.maybeNull(types.string),
    image_src: types.maybeNull(types.string),
    image_url: types.maybeNull(types.string),
    errors: types.maybeNull(types.array(types.string)),
    errorsCorrected: types.maybeNull(types.array(types.string)),
    order: types.maybeNull(types.number),
    touched: types.maybe(types.boolean),
    approved: types.maybe(types.boolean),
    generating: types.maybe(types.boolean),
    rewriting: types.maybe(types.boolean),
    imageChanged: types.maybe(types.boolean),
    questionTypeChanged: types.maybe(types.boolean),
    selected: types.maybe(types.boolean),
    documentversion_id: types.maybeNull(types.number),
    resource_id: types.maybeNull(types.number),
    fact_id: types.maybeNull(types.number),
    generated_question_id: types.maybeNull(types.number),
    source_node_ids: types.maybe(types.array(types.number)),
    removing: types.maybe(types.boolean),
    showStatus: types.maybe(types.boolean),
    final: types.maybe(types.boolean),
    advanced: types.maybe(types.boolean),
    external_id: types.maybeNull(types.string),
    card_json: types.frozen({}),
    card_markdown: types.frozen(),
    card_name: types.frozen(''),
    duplicate_name: types.maybe(types.boolean),
    original_name: types.maybe(types.string),
    is_empty: types.maybe(types.boolean),
    case_sensitive: types.maybe(types.boolean),
  })
  .volatile(() => ({
    imageFile: null as File | null,
  }))
  .actions((self) => ({
    afterCreate() {
      self.shuffle = !self.keep_answer_order
    },
    setEmpty(is_empty: boolean) {
      self.is_empty = is_empty
    },
    setDuplicateName(dupe) {
      if (self.card_id) {
        return
      }
      self.duplicate_name = dupe
    },
    setOriginalName(dupe) {
      self.original_name = dupe
    },
    setCardJSON(json) {
      self.card_json = json
    },
    setCardName(json) {
      self.card_name = json
    },
    setOrder(order: typeof self.order) {
      self.order = order
    },
    select() {
      self.selected = true
    },
    deselect() {
      self.selected = false
    },
    reset() {
      self.text = ''
      self.factoid = Factoid.create({
        text: '',
        factoid_id: null,
        source_id: null,
        generating: undefined,
        modified: false,
      })
      if (self.choices.length < 3) {
        this.addNewChoice()
      }
      self.advanced = false
      self.choices.map((c) => c.reset())
      self.choices[0].set('correct', true)
      self.choices.replace(self.choices.slice(0, 3))
      self.image_src = ''
      self.image_url = ''
      self.alt_text = ''
      self.adaptive_eligible = true
      self.shuffle = true
      self.question_type = 'Multiple Choice'
      self.approved = false
      self.touched = false
      self.documentversion_id = null
      self.fact_id = null
      self.question_id = null
      self.generated_question_id = null
      self.source_node_ids = undefined
    },
    set<K extends keyof SnapshotIn<typeof self>, T extends SnapshotIn<typeof self>>(
      key: K,
      value: T[K],
    ) {
      self[key] = cast(value)
    },
    setApproved(approved: boolean) {
      self.approved = approved
    },
    setTouched(touched: boolean) {
      self.touched = touched
    },
    setShuffleOrder(shuffle: boolean) {
      self.keep_answer_order = !shuffle
      self.shuffle = shuffle
      // self.touched = true
      self.approved = false
    },
    reorderChoices(drag: number, drop: number) {
      const temp = self.choices?.slice()
      if (temp) {
        temp.splice(drop, 0, temp.splice(drag, 1)[0])
        self.choices = cast(temp)
      }
      // self.touched = true
      self.approved = false
    },
    setImage(f: File | null) {
      // The only reason we should ever set this is to set it to a new file or to remove the image
      // otherwise we should receive the image_src from backend on init
      self.image_src = null
      self.imageFile = f
      // self.touched = true
      self.approved = false
      self.imageChanged = true
    },
    setCorrectChoice(choice: Instance<typeof QuestionChoice>, value?: boolean) {
      if (self.question_type === 'Multiple Response') {
        choice.correct = !!value
      } else {
        self.choices.forEach((c) => {
          c.correct = c === choice
        })
      }
      // self.touched = true
      self.approved = false
    },
    toPayload: () => {
      const question = getSnapshot(self)
      const choices = (question.choices ?? [])
        .filter((c) => c.text || c.correct)
        .map((c, index) => ({
          correct: c.correct,
          text: c.text ?? '',
          choice_id: c.choice_id,
          factoid: question?.advanced ? c.factoid : null,
          order: question.keep_answer_order ? index : null,
          pairs: c.pairs,
          ordering: c.ordering?.map((o, i) => ({ ...o, index: i + 1 })),
        })) as any

      return { ...question, errors: [], choices }
    },
    setCaseSensitive(sensitive) {
      self.case_sensitive = sensitive
    },
    addNewChoice(correct?: boolean) {
      self.choices.push(QuestionChoice.create({ correct: correct || false, touched: true }))
      this.set('approved', false)
    },
    addNewOrderedChoice() {
      if (self.choices[0] && self.choices[0].ordering) {
        self.choices[0].ordering.push({ text: '', touched: true })
      }
      this.set('approved', false)
    },
    addNewPair() {
      self.choices?.length &&
        self.choices[0].pairs?.push(ChoicePair.create({ left: '', right: '', touched: true }))
      this.set('approved', false)
    },
    removeChoice(choice: Instance<typeof QuestionChoice>) {
      const filteredChoices = (self.choices ?? ([] as Array<Instance<typeof Question>>)).filter(
        (c) => {
          if (choice === c) {
            return false
          }
          return true
        },
      )

      self.choices = cast(filteredChoices)

      this.set('touched', true)
      this.set('approved', false)
    },
    setQuestionType(type: Instance<typeof QuestionType>) {
      if (type === self.question_type) {
        return
      }

      self.approved = false

      if (self.question_type === 'True or False') {
        self.choices = cast(getChoicesByQuestionType(type))
      } else if (self.question_type === 'Multiple Choice') {
        if (type === 'True or False') {
          self.choices = cast(getChoicesByQuestionType(type))
        }
      } else if (self.question_type === 'Multiple Response') {
        if (type === 'True or False') {
          self.choices = cast(getChoicesByQuestionType(type))
        } else if (type === 'Multiple Choice') {
          let hasCorrect = false
          self.choices.forEach((c) => {
            if (c.correct && hasCorrect) {
              c.correct = false
            } else if (c.correct && !hasCorrect) {
              c.correct = true
              hasCorrect = true
            }
          })

          if (!hasCorrect) {
            self.choices[0].correct = true
          }
        }
      }

      self.question_type = type
    },
  }))
  .views((self) => ({
    get payloadQuestionType() {
      const qtypes = [] as any
      if (self.question_type === 'Multiple Choice') {
        return 'multiple_choice'
      }
      if (self.question_type === 'True or False') {
        return 'true_false'
      }
      if (self.question_type === 'Multiple Response') {
        return 'multiple_choice'
      }
      return qtypes
    },
    get generatingAll() {
      const generating = self.choices.filter((choice) => choice.generating)
      return self.generating || self.factoid?.generating || generating.length > 0
    },
    get formWarnings() {
      const warnings: any = {}

      if (self.text && self.text.length > 1000) {
        warnings.text = {
          type: 'warning',
          message:
            'Question text is over the recommended character limit. It may not display correctly on certain devices.',
        }
      }

      if (self.questionTypeChanged) {
        warnings.questionTypeChanged = {
          type: 'warning',
          message:
            "This question was tagged as True/False, but its answers were not valid True/False. We've changed it to Multiple Choice. You can either change it back to True/False with valid answers or leave it as a Multiple Choice question.",
        }
      }

      if (self.factoid && self.factoid?.text && self.factoid.text.length > 1000) {
        warnings.feedback = {
          type: 'warning',
          message:
            'Feedback is longer than 1000 characters and may not display correctly on some devices.',
        }
      }
      return warnings
    },
    get formErrors() {
      const errors: any = {}

      try {
        const quiz = getParentOfType(self, Quiz) as any
        if (
          quiz &&
          quiz.questions?.filter((q) => !q.removing && q.question_type !== 'Card')?.length === 0
        ) {
          errors.noQuestions = {
            type: 'error',
            message: 'Quizzes must contain at least one question.',
          }
        }
      } catch (e) {
        // console.warn(e)
      }

      if (self.card_name?.length > 60 && self.question_type === 'Card') {
        errors.text = {
          type: 'error',
          message: 'Card name must be 60 characters or less.',
        }
      }

      if (self.duplicate_name && self.question_type === 'Card' && !self.card_id) {
        errors.text = {
          type: 'error',
          message: 'Cannot have a duplicate card name.',
        }
      }

      if (self.is_empty && self.question_type === 'Card') {
        errors.empty = {
          type: 'error',
          message: 'Info cards must have content to publish.',
        }
      }

      if (!self.card_name?.trim().length && self.question_type === 'Card') {
        errors.text = {
          type: 'error',
          message: 'Card name cannot be blank.',
        }
      }

      if (!self.text.trim()?.length && self.question_type !== 'Card') {
        errors.text = {
          type: 'error',
          message: 'Question text cannot be blank.',
        }
      }

      if (
        self.question_type === 'Multiple Choice' &&
        self.choices &&
        (self.choices.length > 4 || self.choices.length < 2)
      ) {
        errors.mc24Choices = {
          type: 'error',
          message: 'Multiple Choice questions must have between 2 and 4 choices.',
        }
      }

      if (self.question_type === 'True or False' && self.choices && self.choices.length !== 2) {
        errors.tf2Choices = {
          type: 'error',
          message: 'True / False questions must have exactly 2 choices.',
        }
      }

      if (
        self.question_type === 'Multiple Response' &&
        self.choices &&
        (self.choices.length > 4 || self.choices.length < 2)
      ) {
        errors.mca24Choices = {
          type: 'error',
          message: 'Multiple Correct Answer questions must have between 2 and 4 choices.',
        }
      }

      if (
        self.question_type === 'Multiple Choice' &&
        self.choices &&
        self.choices.filter((c) => c.correct).length !== 1
      ) {
        errors.mc1Correct = {
          type: 'error',
          message: 'Multiple Choice questions must have exactly 1 correct answer.',
        }
      }

      if (
        self.question_type === 'True or False' &&
        self.choices &&
        self.choices.filter((c) => c.correct).length !== 1
      ) {
        errors.tf1Correct = {
          type: 'error',
          message: 'True / False questions must have exactly 1 correct answer.',
        }
      }

      if (self.question_type === 'Matching') {
        let validPairs = 0
        self.choices?.forEach((choice) => {
          choice.pairs?.forEach((pair) => {
            if (pair.left?.trim().length && pair.right?.trim().length) {
              validPairs++
            }
          })
        })
        if (validPairs < 2) {
          errors.matchingMinPairs = {
            type: 'error',
            message: 'Matching questions must have at least 2 choice pairs.',
          }
        }
      }

      return errors
    },
    get choiceFormErrors() {
      const err = [] as any
      if (self.choices) {
        self.choices.map((choice) => {
          err.push(...choice.allErrors)
        })
      }
      return err
    },
    get choiceFormWarnings() {
      const warnings = [] as any
      self.choices.map((choice) => {
        if (this.new) {
          if (choice.formErrors && choice.touched) {
            warnings.push(...Object.values(choice.formWarnings))
          }
        } else if (choice.formWarnings) {
          warnings.push(...Object.values(choice.formWarnings))
        }
      })
      return warnings
    },
    get allErrors() {
      return [...Object.values(this.formErrors), ...this.choiceFormErrors]
    },
    get allWarnings() {
      return [...Object.values(this.formWarnings), ...this.choiceFormWarnings]
    },
    get new() {
      return !self.question_id
    },
    get image() {
      if (self.imageFile) {
        return URL.createObjectURL(self.imageFile)
      } else if (self.image_src) {
        return self.image_src
      } else if (self.image_url) {
        return self.image_url
      } else {
        return null
      }
    },
  }))
  .actions((self) => ({
    generateChoices: flow(function* (
      distractors_count: number,
      distractors_only?: boolean,
      rejected?: string[],
    ) {
      self.set('generating', true)

      try {
        const correct_choices = self.choices
          .filter((c) => c.correct)
          .map((correctChoice) => correctChoice.text)

        const rejected_choices = rejected
          ? rejected
          : self.choices.filter((c) => !c.correct).map((correctChoice) => correctChoice.text)

        const payload = {
          query: self.text,
          fact: self.factoid?.text || '',
          distractors_count,
          correct_choices,
          rejected_choices,
          generated_question_id: self.generated_question_id,
          distractors_only,
        }

        const result = yield QuestionApi.generateDistractors(payload)
        if (result.ok && result.data) {
          self.set('generating', false)
          return result.data.choices
        } else {
          self.set('generating', false)
          return false
        }
      } catch (error) {
        self.set('generating', false)
        return false
      }
    }),
    generateFeedback: flow(function* () {
      self.factoid?.setGenerating(true)
      try {
        const result = yield QuestionApi.generateFeedback({
          original_fact: self.factoid?.text,
          query: self.text,
          correct_choice: self.choices.filter((c) => c.correct).map((choice) => choice.text),
        })
        if (result.ok && result.data) {
          self.factoid?.setText(result.data.fact)
          self.factoid?.setGenerating(false)
        } else {
          self.factoid?.setGenerating(false)
        }
      } catch (error) {
        self.factoid?.setGenerating(false)
      }
    }),
    generateFromPartial: flow(function* (settings) {
      self.set('generating', true)
      self.set('rewriting', true)
      const exclude_choices = [] as string[]
      const quiz = getParentOfType(self, Quiz) as any
      if (quiz && quiz.title) {
        exclude_choices.push(quiz.title)
      }
      try {
        const result = yield QuestionApi.generateFromPartial({
          documentversion_id: self.documentversion_id,
          generated_question_id: self.generated_question_id,
          fact: self.factoid?.text,
          question: self.text,
          audience: settings.audience,
          voice: settings.voice,
          difficulty:
            settings.difficulty === 'Easy' ? 0 : settings.difficulty === 'Standard' ? 50 : 100,
          question_type: self.payloadQuestionType,
          exclude_choices,
        })

        if (result.ok && result.data) {
          self.set('generating', false)
          self.set('rewriting', false)
          return result.data
        } else {
          self.set('generating', false)
          self.set('rewriting', false)
          return false
        }
      } catch (error) {
        self.set('generating', false)
        self.set('rewriting', false)
        return false
      }
    }),
  }))
